import cn from 'classnames'
import { Nullable } from 'legacy/lut/classes/models/utils'
import { FC, useCallback } from 'react'

import { FormNumberInput, useGetFormDataField } from 'cng-common/features/form'
import { Tooltip } from 'core-components/library/tooltip'

import { PARTNER_BASIC_INFO_FORM_KEY, partnerBasicInfoFields } from '../../../../../configs'
import { TRANSLATION_KEYS } from '../../translations'

import styles from './DistributorInfoEditMode.module.scss'

interface IDistributorInfoEditModeProps {
  totalSales: Nullable<number>
  hpDedicatedSales: Nullable<number>
  employersCount: Nullable<number>
}

const tooltipMessage = 'This value is required for completeness'
export const DistributorInfoEditMode: FC<IDistributorInfoEditModeProps> = ({
  totalSales,
  hpDedicatedSales,
  employersCount,
}) => {
  const [formTotalSales] = useGetFormDataField<Nullable<number>>(
    PARTNER_BASIC_INFO_FORM_KEY,
    partnerBasicInfoFields.totalSales
  )
  const [formHpDedicatedSales] = useGetFormDataField<Nullable<number>>(
    PARTNER_BASIC_INFO_FORM_KEY,
    partnerBasicInfoFields.hpDedicatedSales
  )
  const [formEmployersCount] = useGetFormDataField<Nullable<number>>(
    PARTNER_BASIC_INFO_FORM_KEY,
    partnerBasicInfoFields.employersCount
  )

  const shouldShowCompletenessWarning = useCallback((currentValue: Nullable<number>) => {
    return currentValue === null
  }, [])

  const tooltipRender = useCallback(() => {
    return <>{tooltipMessage}</>
  }, [])

  const totalSalesTooltipCondition =
    shouldShowCompletenessWarning(formTotalSales) && !formTotalSales
  const hpDedicatedSalesTooltipCondition =
    shouldShowCompletenessWarning(formHpDedicatedSales) && !formHpDedicatedSales
  const formEmployersCountTooltipCondition =
    shouldShowCompletenessWarning(formEmployersCount) && !formEmployersCount

  return (
    <div className={styles.root}>
      <div className={styles.fieldWrapper}>
        <Tooltip
          className={styles.tooltipWrapper}
          disabled={!totalSalesTooltipCondition}
          tipContent={tooltipRender}
        >
          <FormNumberInput
            formKey={PARTNER_BASIC_INFO_FORM_KEY}
            field={partnerBasicInfoFields.totalSales}
            label={TRANSLATION_KEYS.totalSalesLabel}
            defaultValue={totalSales}
            className={cn({
              [styles.warning]: totalSalesTooltipCondition,
            })}
          />
        </Tooltip>
      </div>
      <div className={styles.fieldWrapper}>
        <Tooltip
          className={styles.tooltipWrapper}
          disabled={!hpDedicatedSalesTooltipCondition}
          tipContent={tooltipRender}
        >
          <FormNumberInput
            formKey={PARTNER_BASIC_INFO_FORM_KEY}
            field={partnerBasicInfoFields.hpDedicatedSales}
            label={TRANSLATION_KEYS.hpDedicatedSalesLabel}
            defaultValue={hpDedicatedSales}
            className={cn({
              [styles.warning]: hpDedicatedSalesTooltipCondition,
            })}
          />
        </Tooltip>
      </div>
      <div className={styles.fieldWrapper}>
        <Tooltip
          className={styles.tooltipWrapper}
          disabled={!formEmployersCountTooltipCondition}
          tipContent={tooltipRender}
        >
          <FormNumberInput
            formKey={PARTNER_BASIC_INFO_FORM_KEY}
            field={partnerBasicInfoFields.employersCount}
            label={TRANSLATION_KEYS.employersCountLabel}
            defaultValue={employersCount}
            className={cn({
              [styles.warning]: formEmployersCountTooltipCondition,
            })}
          />
        </Tooltip>
      </div>
    </div>
  )
}
